import { Injectable } from '@angular/core';
import { fromEvent, throwError } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';

import { ToastService } from '../../toast/toast.service';
import { environment } from '../../../environments/environment';
import { LanguageService } from './language.service';
import { ToastRef } from '../../toast/toast-ref';

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  constructor(
    private readonly cookieService: CookieService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly languageService: LanguageService) {
  }

  static getLanguagePath() {
    return location.pathname.slice(1, 3);
  }

  static handleAppLanguage(language: string): boolean|string {
    if (!environment.production) {
      return false;
    }

    const languageCode = UtilService.getLanguagePath();
    const indexOf = environment.languageSettings.availableLanguages.indexOf(languageCode) >= 0;

    if (indexOf && language !== languageCode) {
      return UtilService.redirectionURI(language);
    }

    if (!indexOf && language !== 'de') {
      return `/${language}/index.html`;
    }

    return false;
  }

  static redirectTo(url: string) {
    location.replace(url);
  }

  static redirectionURI(language: string) {
    return (language === 'de') ? '/' : `/${language}/index.html`;
  }

  private isAnalyticsDisabledWithCookies() {
    return this.cookieService.get(environment.cookieNameAnalytics) === 'true';
  }

  navigateByUrlWithSkipLocationChangeEnabled(url) {
    this.router.navigateByUrl('/', { skipLocationChange: true })
    .then(() => this.router.navigateByUrl(url));
  }

  getWindowResizeAction() {
    return fromEvent(window, 'resize').pipe(debounceTime(200));
  }

  copyContentToClipBoard(content) {
    const createCopy = (e: ClipboardEvent) => {
      e.clipboardData.setData('text/plain', content);
      e.preventDefault();
    };
    document.addEventListener('copy', createCopy);
    document.execCommand('copy');
    document.removeEventListener('copy', createCopy);
  }

  showToastWarning(error, delay = 0) {
    if (!error) {
      return;
    }
    const [e] = error?.errors ?? [];

    setTimeout(() => {
      this.toastService.show({
        text: e?.details,
        language: this.languageService.getCurrentLanguage(),
        type: 'warning'
      });
    }, delay);

    return throwError(e);
  }

  showToastInfo(info, delay = 0) {
    if (!info) {
      return;
    }

    setTimeout(() => {
      this.toastService.show({
        text: info?.details,
        type: 'info'
      });
    }, delay);
  }

  showDownloadToastInfo(info): ToastRef {
    if (!info) {
      return;
    }

    return this.toastService.show({
      text: info?.details,
      type: 'info',
      configOverrides: { closeAfter: 0 }
    });
  }

  appendGATrackingPlan() {
    if (this.isAnalyticsDisabledWithCookies()) {
      return;
    }

    try {
      const script = document.createElement('script');
      script.setAttribute('nonce', 'o9bideq5pvw4pdvmqn1mbpdb0omhemrp');
      script.id = 'trackingplan-script';
      script.async = true;
      script.src = 'https://sdk.trackingplan.com/js/a/trackingplan-se-common.js';
      script.setAttribute('data-initialize', 'true');
      script.setAttribute('data-tpid', 'TP4790407');
      script.setAttribute('data-environment', 'PRODUCTION');

      document.head.insertBefore(script, document.head.firstChild);
    } catch (ex) {
      console.error('Error appending google analytics tracking plan');
      console.error(ex);
    }
  }

  // Google Tag Manager
  appendGATrackingCode(trackingCode: string) {
    if (!trackingCode || this.isAnalyticsDisabledWithCookies()) {
      return;
    }
    try {
      const scriptCdn = document.createElement('script');
      scriptCdn.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
      scriptCdn.type = 'text/javascript';
      scriptCdn.charset = 'UTF-8';
      scriptCdn.setAttribute('data-domain-script', environment.dataDomainScript);
      const nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce || '';
      if (nonce.length) {
        scriptCdn.setAttribute('nonce', nonce);
      }

      document.head.appendChild(scriptCdn);

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML =
        `
      // Based on the SE/OneTrust "gtmController" snippet. Deviations: policy links are
      // language-aware (de/fr/it) and click handlers bind once (OptanonWrapper re-runs).
      const gtmController = {
        // Replaced per request by the .NET SPA fallback (same value as the CSP header nonce).
        currentNonce: '${nonce}',
        gtmId: '${trackingCode}',
        cookiePolicyUrls: {
          de: 'https://www.feller.ch/de/cookie-policy',
          fr: 'https://www.feller.ch/fr/cookie-policy',
          it: 'https://www.feller.ch/it/cookie-policy',
        },
        privacyPolicyUrls: {
          de: 'https://www.feller.ch/de/datenschutzerklaerung',
          fr: 'https://www.feller.ch/fr/declaration-de-protection-des-donnees',
          it: 'https://www.feller.ch/it/informativa-sulla-privacy',
        },
        gtmLoaded: false,
        pcLoadedListenerAdded: false,

        // Priority mirrors the SPA: URL path segment > stored i18nextLng > 'de'.
        resolveLanguage: function () {
          const supported = ['de', 'fr', 'it']
          const urlLang = (window.location.pathname.split('/')[1] || '')
            .slice(0, 2)
            .toLowerCase()
          if (supported.indexOf(urlLang) !== -1) return urlLang
          const stored = (localStorage.getItem('i18nextLng') || '')
            .slice(0, 2)
            .toLowerCase()
          if (supported.indexOf(stored) !== -1) return stored
          return 'de'
        },
        bindOnce: function (element, handler) {
          if (!element || element.dataset.otBound) return
          element.dataset.otBound = 'true'
          element.addEventListener('click', handler)
        },
        bindPolicyLink: function (elementId, urls) {
          const self = this
          this.bindOnce(document.getElementById(elementId), function (e) {
            e.preventDefault()
            // Resolved on click so an in-app language switch is respected.
            window.open(urls[self.resolveLanguage()], '_blank')
          })
        },
        setPrivacyLinks: function () {
          // Stop the '#' jump of the 3rd-party vendor list link.
          this.bindOnce(
            document.querySelector('.ot-link-btn.ot-gv-list-handler'),
            function (e) {
              e.preventDefault()
            },
          )
          this.bindPolicyLink('otPrivacyPolicy', this.privacyPolicyUrls)
          this.bindPolicyLink('otCookiePolicy', this.cookiePolicyUrls)
        },
        hasAnyOtConsent: function (categoryIds) {
          if (!categoryIds) {
            categoryIds = ['C0002', 'C0003', 'C0004']
          }
          function getCookie(name) {
            const parts = ('; ' + document.cookie).split('; ' + name + '=')
            if (parts.length === 2) {
              return parts.pop().split(';').shift()
            }
            return null
          }

          const optanonCookie = getCookie('OptanonConsent')

          // Fallback to OnetrustActiveGroups if the consent cookie isn't available.
          if (!optanonCookie) {
            const activeGroups = window.OnetrustActiveGroups || ''
            for (let i = 0; i < categoryIds.length; i++) {
              if (activeGroups.indexOf(categoryIds[i]) !== -1) {
                return true
              }
            }
            return false
          }

          const decodedCookie = decodeURIComponent(optanonCookie)
          // Any requested category explicitly active (e.g. 'C0002:1').
          for (let j = 0; j < categoryIds.length; j++) {
            if (new RegExp(categoryIds[j] + ':1').test(decodedCookie)) {
              return true
            }
          }
          return false
        },
        inject: function () {
          if (this.gtmLoaded || !this.hasAnyOtConsent()) return
          this.gtmLoaded = true
          ;(function (w, d, s, l, i, currentNonce) {
            w[l] = w[l] || []
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
            const f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : ''
            j.async = true
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
            // Propagate the CSP nonce so GTM stamps it on the scripts it injects.
            if (currentNonce) {
              j.setAttribute('nonce', currentNonce)
            }
            f.parentNode.insertBefore(j, f)
          })(
            window,
            document,
            'script',
            'dataLayer',
            this.gtmId,
            this.currentNonce,
          )
        },
      }

      // Returning visitor with stored consent: load GTM without waiting for the OneTrust SDK.
      gtmController.inject()

      function OptanonWrapper() {
        const bannerClosed = !!(
          window.OneTrust && OneTrust.IsAlertBoxClosedAndValid()
        )
        const otDomainData =
          window.OneTrust && typeof window.OneTrust.GetDomainData == 'function'
            ? window.OneTrust.GetDomainData()
            : false
        const otOptOut = !!(
          otDomainData &&
          otDomainData.ConsentModel &&
          otDomainData.ConsentModel.Name == 'opt-out'
        )

        if (!gtmController.pcLoadedListenerAdded) {
          gtmController.pcLoadedListenerAdded = true
          window.addEventListener('OneTrustPCLoaded', function () {
            gtmController.setPrivacyLinks()
          })
        }

        setTimeout(function () {
          if (!bannerClosed) {
            gtmController.setPrivacyLinks()
          }
        }, 1500)

        // Retry injection once consent was given (banner closed) or the region is opt-out.
        setTimeout(function () {
          if (bannerClosed || otOptOut) {
            gtmController.inject()
          }
        }, 100)
      }
    `;

      if (nonce.length) {
        script.setAttribute('nonce', nonce);
      }
      document.head.appendChild(script);
    } catch (ex) {
      console.error('Error appending google analytics');
      console.error(ex);
    }
  }

  // Google Analytics
  appendGoogleAnalytics(gaCode: string) {
    if (!gaCode || this.isAnalyticsDisabledWithCookies()) {
      return;
    }

    const gaScript = document.createElement('script');
    gaScript.setAttribute('nonce', '3d9f7b4a6e5c8d2f1a0b7c6e8d9a5f4b');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaCode}`;

    const gaScript2 = document.createElement('script');
    // eslint-disable-next-line no-useless-escape, max-len
    gaScript2.innerText = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag(\'js\', new Date());gtag(\'config\', \'${ gaCode }\');`;
    gaScript2.setAttribute('nonce', 'b6d7e8a5c4f1b3d9a2c6e7d8b4a5f1c');

    document.head.appendChild(gaScript);
    document.documentElement.firstChild.appendChild(gaScript2);
  }

  // Google Tag Manager NoScript In Body
  appendGATrackingCodeInBody(trackingCode: string) {
    if (!trackingCode || this.isAnalyticsDisabledWithCookies()) {
      return;
    }

    const noscript = document.createElement('noscript');
    noscript.setAttribute('nonce', 'jb9cnlru60j50cjdhwmv8cib84orx3s8');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${trackingCode}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.classList.add('visibility-hidden');
    iframe.classList.add('hidden');
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);
  }

}
